import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { getStatementManualAccounts, getStatementSources } from "@/lib/api";
import { ManualAccountsView } from "@/pages/StatementsPage";

const currentFiscalYear = () => {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

export default function FinancialInputsPage() {
  const [year, setYear] = useState(currentFiscalYear());
  const [data, setData] = useState({ accounts: [], months: [], capital_accounts: [] });
  const [taxonomy, setTaxonomy] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inputsResult, sourcesResult] = await Promise.all([
        getStatementManualAccounts(year),
        getStatementSources(),
      ]);
      setData(inputsResult.data || { accounts: [], months: [], capital_accounts: [] });
      setTaxonomy(sourcesResult.data?.taxonomy || {});
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load Financial Inputs");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  return <div className="p-4 md:p-6 space-y-5" data-testid="financial-inputs-page">
    <header className="rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.09] via-white/[0.025] to-cyan-500/[0.06] p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[.2em] text-violet-300"><WalletCards size={14}/> Regular financial inputs</div>
          <h1 className="text-2xl md:text-3xl font-bold mt-2">Financial Inputs</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-3xl">Maintain business income, wallet balances and realised capital gains or losses without entering the statement-import workspace.</p>
        </div>
        <button onClick={load} disabled={loading} className="rounded-lg border border-white/10 px-3 py-2 text-xs inline-flex items-center gap-2 hover:bg-white/5 disabled:opacity-50"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Refresh</button>
      </div>
    </header>
    {loading && !data.accounts?.length && !data.capital_accounts?.length
      ? <div className="h-48 flex items-center justify-center gap-2 text-cyan-300"><Loader2 className="animate-spin" size={18}/> Loading financial inputs…</div>
      : <ManualAccountsView data={data} year={year} setYear={setYear} reload={load} taxonomy={taxonomy}/>}
  </div>;
}
